import React, { useEffect, useState, useRef, useMemo } from "react";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, EmptyState } from "@/components/ui-kit";
import InspireBanner from "@/components/InspireBanner";
import SchoolFilter, { useSchools } from "@/components/SchoolFilter";
import { Images, Trash2, X, UploadCloud, HandHeart } from "lucide-react";
import { toast } from "sonner";
import { playSuccess } from "@/lib/sound";

const readFile = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

// Compress a gallery image to a JPEG data-url under maxKB
const compressImage = (file, maxKB = 500) => new Promise((resolve) => {
  if (!file.type.startsWith("image/")) return resolve(null);
  const reader = new FileReader();
  reader.onerror = () => resolve(null);
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => resolve(null);
    img.onload = () => {
      let { width, height } = img;
      const maxDim = 1600;
      if (Math.max(width, height) > maxDim) {
        const s = maxDim / Math.max(width, height);
        width = Math.round(width * s); height = Math.round(height * s);
      }
      const canvas = document.createElement("canvas");
      const draw = () => { canvas.width = width; canvas.height = height; canvas.getContext("2d").drawImage(img, 0, 0, width, height); };
      const sizeKB = (u) => (u.length * 3) / 4 / 1024;
      draw();
      let q = 0.85;
      let out = canvas.toDataURL("image/jpeg", q);
      while (sizeKB(out) > maxKB && q > 0.3) { q -= 0.1; out = canvas.toDataURL("image/jpeg", q); }
      resolve(out);
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

export default function Gallery() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const schools = useSchools();
  const [items, setItems] = useState([]);
  const [opps, setOpps] = useState([]);
  const [schoolF, setSchoolF] = useState("");
  const [selectedOpp, setSelectedOpp] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const load = () => {
    const qp = isAdmin && schoolF ? `?school_id=${schoolF}` : "";
    api.get(`/photos${qp}`).then((r) => setItems(r.data)).catch(() => {});
    api.get(`/opportunities${qp}`).then((r) => setOpps(r.data || [])).catch(() => {});
  };
  useEffect(load, [schoolF, isAdmin]);

  const oppName = useMemo(() => {
    const m = {}; opps.forEach((o) => { m[o.id] = o.name; }); return m;
  }, [opps]);

  // Group photos by opportunity
  const groups = useMemo(() => {
    const g = {};
    items.forEach((p) => {
      const key = p.opportunity_id || "__none__";
      (g[key] = g[key] || []).push(p);
    });
    return g;
  }, [items]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    if (!selectedOpp) { toast.error("اختر الفرصة التطوعية أولاً قبل رفع صورها"); return; }
    if (files.length > 5) { toast.error("يمكن رفع 5 صور كحد أقصى دفعة واحدة"); return; }
    const oversize = files.find((f) => f.size > 3 * 1024 * 1024);
    if (oversize) { toast.error("كل صورة يجب أن تكون أقل من 3 ميجا"); return; }
    setUploading(true);
    try {
      for (const f of files) {
        const image = (await compressImage(f, 500)) || (await readFile(f));
        await api.post("/photos", { title: f.name.replace(/\.[^.]+$/, ""), image, opportunity_id: selectedOpp, category: "أعمال تطوعية", date: new Date().toISOString().slice(0, 10) });
      }
      playSuccess();
      toast.success(`تم رفع ${files.length} صورة لفرصة «${oppName[selectedOpp] || ""}»`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); };

  const remove = async (id) => {
    try { await api.delete(`/photos/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const orderedKeys = Object.keys(groups).sort((a, b) => (a === "__none__" ? 1 : b === "__none__" ? -1 : 0));

  return (
    <div>
      <InspireBanner />

      <PageTitle title="معرض الأعمال التطوعية" subtitle="وثّق إنجازاتكم — صور كل فرصة مجمّعة تحت اسمها 🌟" icon={Images} />

      {isAdmin && <div className="glass rounded-2xl p-4 mb-5"><SchoolFilter value={schoolF} onChange={setSchoolF} schools={schools} /></div>}

      {!isAdmin && (
        <div className="mb-6">
          <label className="text-xs text-stone-500 mb-1 block">اختر الفرصة التطوعية لربط الصور بها</label>
          <select data-testid="gallery-opp-select" value={selectedOpp} onChange={(e) => setSelectedOpp(e.target.value)}
            className="w-full mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-400">
            <option value="">— اختر الفرصة —</option>
            {opps.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <div
            data-testid="gallery-dropzone"
            onClick={() => { if (!selectedOpp) return toast.error("اختر الفرصة التطوعية أولاً"); fileRef.current?.click(); }}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            className={`rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition ${drag ? "border-sky-500 bg-sky-50" : "border-amber-400/40 glass glass-hover"}`}
          >
            <UploadCloud size={38} className="mx-auto text-sky-600 mb-2" />
            <p className="text-stone-700 font-semibold">اسحب وأفلت صور الفرصة هنا أو اضغط للاختيار</p>
            <p className="text-xs text-stone-500 mt-1">{selectedOpp ? `الصور ستُربط بفرصة: ${oppName[selectedOpp] || ""}` : "اختر الفرصة أولاً — حتى 5 صور دفعة واحدة"}</p>
            {uploading && <p className="text-sky-600 text-sm mt-2">جارٍ الرفع...</p>}
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => handleFiles(e.target.files)} />
          </div>
        </div>
      )}

      {!items.length ? <EmptyState text="لا توجد أعمال بعد — اختر فرصة وابدأ برفع صورها!" icon={Images} /> : (
        <div className="space-y-8">
          {orderedKeys.map((key) => (
            <div key={key} data-testid={`opp-group-${key}`}>
              <div className="flex items-center gap-2 mb-3">
                <HandHeart size={18} className="text-amber-600" />
                <h3 className="font-head font-bold text-stone-800">{key === "__none__" ? "أعمال عامة (غير مرتبطة بفرصة)" : (oppName[key] || "فرصة محذوفة")}</h3>
                <span className="text-xs text-stone-400">({groups[key].length} صورة)</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {groups[key].map((p) => (
                  <div key={p.id} className="glass glass-hover rounded-2xl overflow-hidden group relative">
                    <img src={p.image} alt={p.title} onClick={() => setLightbox(p)} className="w-full h-40 object-cover cursor-pointer" />
                    <div className="p-3">
                      <h4 className="text-sm font-semibold text-stone-800 truncate">{p.title}</h4>
                      <p className="text-xs text-stone-500 truncate">{p.date}</p>
                    </div>
                    {!isAdmin && (
                      <button data-testid={`del-photo-${p.id}`} onClick={() => remove(p.id)} className="absolute top-2 left-2 p-1.5 rounded-lg bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition" title="حذف"><Trash2 size={15} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-5 left-5 text-white/80 hover:text-white z-10"><X size={30} /></button>
          <div className="max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.image} alt={lightbox.title} className="max-h-[80vh] rounded-xl" />
            <div className="text-center mt-3 text-white"><h3 className="font-head font-bold text-lg">{lightbox.title}</h3></div>
          </div>
        </div>
      )}
    </div>
  );
}
